/**
 * Pure markdown formatting helpers used by the markdown editor toolbar.
 *
 * Given the current textarea value plus the selection range, `applyMarkdownFormat`
 * returns the new value and where the selection should land afterwards. It has no
 * React/DOM dependency so it can be unit-tested in isolation.
 */

export type MarkdownAction =
  | 'heading'
  | 'bold'
  | 'italic'
  | 'quote'
  | 'code'
  | 'link'
  | 'unordered-list'
  | 'ordered-list'
  | 'task-list';

export interface MarkdownFormatResult {
  text: string;
  selectionStart: number;
  selectionEnd: number;
}

const INLINE_MARKERS: Partial<Record<MarkdownAction, { marker: string; placeholder: string }>> = {
  bold: { marker: '**', placeholder: 'texto en negrita' },
  italic: { marker: '*', placeholder: 'texto en cursiva' },
  code: { marker: '`', placeholder: 'código' },
};

const LINE_PREFIXES: Partial<Record<MarkdownAction, string>> = {
  heading: '# ',
  quote: '> ',
  'unordered-list': '- ',
  'task-list': '- [ ] ',
};

/**
 * Wrap the selection with an inline marker (bold/italic/code). Toggles the marker
 * off when the selection is already wrapped. With an empty selection, inserts the
 * marker around a Spanish placeholder and selects the placeholder.
 */
const applyInlineWrap = (
  value: string,
  start: number,
  end: number,
  marker: string,
  placeholder: string
): MarkdownFormatResult => {
  const before = value.slice(0, start);
  const after = value.slice(end);
  const selected = value.slice(start, end);

  // Toggle off: the selected text itself contains the markers.
  if (
    selected.length >= marker.length * 2 &&
    selected.startsWith(marker) &&
    selected.endsWith(marker)
  ) {
    const inner = selected.slice(marker.length, selected.length - marker.length);
    return {
      text: before + inner + after,
      selectionStart: start,
      selectionEnd: start + inner.length,
    };
  }

  // Toggle off: the markers surround the selection.
  if (before.endsWith(marker) && after.startsWith(marker)) {
    const newBefore = before.slice(0, before.length - marker.length);
    const newAfter = after.slice(marker.length);
    const newStart = start - marker.length;
    return {
      text: newBefore + selected + newAfter,
      selectionStart: newStart,
      selectionEnd: newStart + selected.length,
    };
  }

  // Empty selection: insert placeholder and select it.
  if (selected.length === 0) {
    const newStart = start + marker.length;
    return {
      text: before + marker + placeholder + marker + after,
      selectionStart: newStart,
      selectionEnd: newStart + placeholder.length,
    };
  }

  // Wrap the current selection.
  const newStart = start + marker.length;
  return {
    text: before + marker + selected + marker + after,
    selectionStart: newStart,
    selectionEnd: newStart + selected.length,
  };
};

/**
 * Insert a markdown link. With a selection it becomes the link text and `url` is
 * selected; with an empty selection `texto` is selected so the user can type first.
 */
const applyLink = (value: string, start: number, end: number): MarkdownFormatResult => {
  const before = value.slice(0, start);
  const after = value.slice(end);
  const selected = value.slice(start, end);

  if (selected.length === 0) {
    // `[texto](url)` with `texto` selected.
    const textStart = start + 1;
    return {
      text: `${before}[texto](url)${after}`,
      selectionStart: textStart,
      selectionEnd: textStart + 'texto'.length,
    };
  }

  // `[selección](url)` with `url` selected.
  const inserted = `[${selected}](url)`;
  const urlStart = start + 1 + selected.length + 2; // '[' + selected + ']('
  return {
    text: before + inserted + after,
    selectionStart: urlStart,
    selectionEnd: urlStart + 'url'.length,
  };
};

const ORDERED_PREFIX = /^\d+\. /;

const hasLinePrefix = (line: string, action: MarkdownAction): boolean => {
  if (action === 'ordered-list') return ORDERED_PREFIX.test(line);
  const prefix = LINE_PREFIXES[action];
  return prefix ? line.startsWith(prefix) : false;
};

const removeLinePrefix = (line: string, action: MarkdownAction): string => {
  if (action === 'ordered-list') return line.replace(ORDERED_PREFIX, '');
  const prefix = LINE_PREFIXES[action];
  return prefix && line.startsWith(prefix) ? line.slice(prefix.length) : line;
};

/**
 * Add or remove a line prefix (heading/quote/lists) across every line the selection
 * touches. Toggles off when all covered lines already have the prefix. Ordered lists
 * number their lines sequentially.
 */
const applyLinePrefixFormat = (
  value: string,
  start: number,
  end: number,
  action: MarkdownAction
): MarkdownFormatResult => {
  const lineStart = value.lastIndexOf('\n', start - 1) + 1;
  let lineEnd = value.indexOf('\n', end);
  if (lineEnd === -1) lineEnd = value.length;

  const before = value.slice(0, lineStart);
  const after = value.slice(lineEnd);
  const lines = value.slice(lineStart, lineEnd).split('\n');

  const allPrefixed = lines.every((line) => hasLinePrefix(line, action));

  let newLines: string[];
  if (allPrefixed) {
    newLines = lines.map((line) => removeLinePrefix(line, action));
  } else if (action === 'ordered-list') {
    newLines = lines.map((line, index) => `${index + 1}. ${removeLinePrefix(line, action)}`);
  } else {
    const prefix = LINE_PREFIXES[action] ?? '';
    newLines = lines.map((line) => (hasLinePrefix(line, action) ? line : prefix + line));
  }

  const newBlock = newLines.join('\n');
  const text = before + newBlock + after;
  const blockEnd = lineStart + newBlock.length;

  // Collapsed cursor lands at the end of the block; a real selection keeps the block selected.
  if (start === end) {
    return { text, selectionStart: blockEnd, selectionEnd: blockEnd };
  }
  return { text, selectionStart: lineStart, selectionEnd: blockEnd };
};

export const applyMarkdownFormat = (
  value: string,
  selectionStart: number,
  selectionEnd: number,
  action: MarkdownAction
): MarkdownFormatResult => {
  const start = Math.min(selectionStart, selectionEnd);
  const end = Math.max(selectionStart, selectionEnd);

  if (action === 'link') return applyLink(value, start, end);

  const inline = INLINE_MARKERS[action];
  if (inline) return applyInlineWrap(value, start, end, inline.marker, inline.placeholder);

  return applyLinePrefixFormat(value, start, end, action);
};

const TASK_ITEM = /^(\s*)- \[[ xX]\] (.*)$/;
const ORDERED_ITEM = /^(\s*)(\d+)\. (.*)$/;
const UNORDERED_ITEM = /^(\s*)([-*+]) (.*)$/;

/**
 * List continuation. When Enter is pressed with a collapsed cursor on a
 * list item, returns the new value with the next marker inserted; if the item is empty
 * (only the marker), the marker is removed so the user exits the list. Returns `null`
 * when the current line is not a list item (let the default newline happen).
 */
export const continueListOnEnter = (
  value: string,
  selectionStart: number,
  selectionEnd: number
): MarkdownFormatResult | null => {
  if (selectionStart !== selectionEnd) return null;

  const cursor = selectionStart;
  const lineStart = value.lastIndexOf('\n', cursor - 1) + 1;
  let lineEnd = value.indexOf('\n', cursor);
  if (lineEnd === -1) lineEnd = value.length;
  const line = value.slice(lineStart, lineEnd);

  // Task items must be checked before unordered ones, since "- [ ] x" also matches "- x".
  let nextPrefix: string | null = null;
  let content = '';
  const task = TASK_ITEM.exec(line);
  const ordered = ORDERED_ITEM.exec(line);
  const unordered = UNORDERED_ITEM.exec(line);
  if (task) {
    [, , content] = task;
    nextPrefix = `${task[1]}- [ ] `;
  } else if (ordered) {
    [, , , content] = ordered;
    nextPrefix = `${ordered[1]}${Number(ordered[2]) + 1}. `;
  } else if (unordered) {
    [, , , content] = unordered;
    nextPrefix = `${unordered[1]}${unordered[2]} `;
  }

  if (nextPrefix === null) return null;

  // Empty item: clear the marker and stay on the (now empty) line to exit the list.
  if (content.trim() === '') {
    return {
      text: value.slice(0, lineStart) + value.slice(lineEnd),
      selectionStart: lineStart,
      selectionEnd: lineStart,
    };
  }

  // Non-empty item: split at the cursor and start a new item with the next marker.
  const insertion = `\n${nextPrefix}`;
  const newCursor = cursor + insertion.length;
  return {
    text: value.slice(0, cursor) + insertion + value.slice(cursor),
    selectionStart: newCursor,
    selectionEnd: newCursor,
  };
};
